import React, { useState } from 'react';

import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import { MainCard } from '~/components/core/NewDesignSystem/Cards/MainCard/MainCard';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import useOrganization from '~/components/OrganizationContext';
import type { ClaimFlagConfigurationCreateJson } from '~/server_shared/openapi/data-contracts';

import type { ClaimFlagConfiguration } from '../../../ClaimFlags/types';

import ClaimFlagAddEditDialog from './components/ClaimFlagAddEditDialog';
import ClaimFlagConfigurationEmptyState from './components/ClaimFlagConfigurationEmptyState';
import FlagConfigRow, { flagRowGrid } from './components/FlagConfigRow';
import useClaimFlagConfigurationAndMutations from './hooks/useClaimFlagConfigurationAndMutations';
import TEXTS from './texts';

const ClaimFlagConfigurationHeader = () => {
  const { subOrganizationEnabled } = useOrganization() as { subOrganizationEnabled?: boolean };
  return (
    <div className={`${flagRowGrid(!!subOrganizationEnabled)} border-b border-slate-300 px-8 py-8`}>
      <span />
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.columns.color}
      </Text>
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.columns.key}
      </Text>
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.columns.label}
      </Text>
      {subOrganizationEnabled && (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.columns.subOrgs}
        </Text>
      )}
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.columns.lobs}
      </Text>
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="justify-self-end">
        {TEXTS.columns.enabled}
      </Text>
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="justify-self-end">
        {TEXTS.columns.actions}
      </Text>
    </div>
  );
};

const ClaimFlagConfiguration = () => {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    editedFlag: ClaimFlagConfiguration | null;
  }>({ isOpen: false, editedFlag: null });
  const { claimFlagConfigurations, isLoading, isError, isUpdating, create, update, reorder } =
    useClaimFlagConfigurationAndMutations();

  // Local order so a drop reorders instantly; resynced to the server whenever the fetched data
  // changes, which also reverts a failed reorder. The resync happens during render (not in an
  // effect) so the new order is reflected in the same paint — otherwise the empty-state/list gate
  // below would read a stale empty list for one post-load paint and briefly flash the empty state.
  const [orderedFlags, setOrderedFlags] = useState(claimFlagConfigurations);
  const [syncedConfigurations, setSyncedConfigurations] = useState(claimFlagConfigurations);
  if (syncedConfigurations !== claimFlagConfigurations) {
    setSyncedConfigurations(claimFlagConfigurations);
    setOrderedFlags(claimFlagConfigurations);
  }

  const closeDialog = () => setDialogState({ isOpen: false, editedFlag: null });
  const handleCreateClick = () => setDialogState({ isOpen: true, editedFlag: null });
  const handleEditClick = (flag: ClaimFlagConfiguration) => setDialogState({ isOpen: true, editedFlag: flag });

  const handleSubmit = async (values: ClaimFlagConfigurationCreateJson) => {
    try {
      if (dialogState.editedFlag) {
        // key is immutable and absent from the update schema — send only the mutable fields so the
        // PATCH body matches the contract (and wouldn't 422 under a stricter extra='forbid').
        const { key: _key, ...updateValues } = values;
        await update(dialogState.editedFlag.key, updateValues);
      } else {
        await create(values);
      }
      closeDialog();
    } catch {
      // The mutation already surfaced the error; keep the dialog open so the admin's input isn't lost.
    }
  };

  const handleToggle = (flag: ClaimFlagConfiguration) =>
    // Fire-and-forget from the Switch's onChange: runMutation already reports the error and resyncs the
    // list in its finally, so swallow the rethrow here to avoid an unhandled promise rejection.
    update(flag.key, { is_enabled: !flag.is_enabled }).catch(() => {});

  const handleReorder = async ({ fromIndex, toIndex }: DndOrderedListReorder) => {
    const reordered = [...orderedFlags];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setOrderedFlags(reordered);

    try {
      await reorder(reordered.map((flag) => flag.key));
    } catch {
      // Revert the optimistic move back to the server order. The render-resync above only reverts when
      // the reload returns a new array reference; if it returns the same reference (the fetch hook hands
      // back a referentially-stable EMPTY on an empty list), the `!==` guard skips it, so revert here too.
      setOrderedFlags(claimFlagConfigurations);
    }
  };

  return (
    <MainCard type="elevated" title={TEXTS.sectionTitle}>
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="-mt-4">
        {TEXTS.sectionDescription}
      </Text>
      {isLoading || isError ? (
        <SkeletonTable rowsCount={6} columnsCount={1} isError={isError} />
      ) : orderedFlags.length === 0 ? (
        <ClaimFlagConfigurationEmptyState onCreateClick={handleCreateClick} />
      ) : (
        <>
          <Button color="primary" onClick={handleCreateClick} className="self-end" disabled={isUpdating}>
            <AddIcon />
            {TEXTS.addFlag}
          </Button>
          <div>
            <ClaimFlagConfigurationHeader />
            <DndOrderedList
              className="flex flex-col gap-8"
              disabled={isUpdating}
              items={orderedFlags}
              getItemId={(flag) => flag.key}
              renderItem={(flag, { dragHandleProps }) => (
                <FlagConfigRow
                  flag={flag}
                  dragHandleProps={dragHandleProps}
                  onToggle={() => handleToggle(flag)}
                  onEdit={() => handleEditClick(flag)}
                  disabled={isUpdating}
                />
              )}
              onReorder={handleReorder}
            />
          </div>
        </>
      )}

      <ClaimFlagAddEditDialog
        isOpen={dialogState.isOpen}
        editedFlag={dialogState.editedFlag}
        onClose={closeDialog}
        onSubmit={handleSubmit}
        existingKeys={orderedFlags.map((flag) => flag.key)}
      />
    </MainCard>
  );
};

export default ClaimFlagConfiguration;
